import { CopyIcon } from 'assets/images/icons';
import { CheckIcon, ExternalLinkIcon } from 'common/ui/icons';
import _ from 'lodash';
import { useState } from 'react';
import { CheckmarkIcon } from 'react-hot-toast';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { DomainDto } from '../../../../common/interfaces/domains';
import { utilPagePathGenerator } from '../../../../common/utils';
import { PageType } from '../../../../context/page-form-context';
import styles from './success-page.module.scss';

interface LinkItemProps {
  isInternal?: boolean;
  to?: string;
  url: string;
}

const LinkItem = ({ isInternal, to, url }: LinkItemProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.link_item}>
      <div className={styles.link_content}>
        {isInternal && to ? (
          <Link className={styles.link} to={to}>
            {url}
          </Link>
        ) : (
          <a className={styles.link} href={url} rel="noreferrer" target="_blank">
            {url}
            <ExternalLinkIcon />
          </a>
        )}
      </div>
      <button
        className={`${styles.copy_btn} ${copied ? styles.copied : ''}`}
        onClick={handleCopy}
        title="Copy to clipboard"
        type="button"
      >
        {copied ? <CheckmarkIcon /> : <CopyIcon />}
        <span>{copied ? 'Copied!' : 'Copy'}</span>
      </button>
    </div>
  );
};

interface LinkSectionProps {
  children: React.ReactNode;
  description?: string;
  title: string;
}

const LinkSection = ({ children, description, title }: LinkSectionProps) => (
  <div className={styles.section}>
    <div className={styles.section_header}>
      <h3 className={styles.section_title}>{title}</h3>
      {description && <p className={styles.section_description}>{description}</p>}
    </div>
    <div className={styles.section_content}>
      {children}
    </div>
  </div>
);

export const SuccessPage = () => {
  const location = useLocation();
  const {
    brand,
    domain,
    domains,
    layout,
    marketplace,
    publisher,
    slug,
  } = location.state;

  const localeLinkToEditing = utilPagePathGenerator({
    brand,
    params: {
      domain,
      layout,
      marketplace,
    },
    publisher,
    slug,
  });

  const absoluteLinkToEditing = utilPagePathGenerator(
    {
      brand,
      params: {
        domain,
        layout,
        marketplace,
      },
      publisher,
      slug,
    },
    {
      absolute: true,
      public: false,
    },
  );

  const publicLinks = domains
    .map((domainItem: DomainDto) => (
      {
        id: domainItem.id,
        url: utilPagePathGenerator(
          {
            brand,
            publisher,
            slug,
          },
          {
            absolute: true,
            domain: domainItem.name,
            public: true,
            ...(layout === PageType.Social && { template: 'social' }),
          },
        ),
      }
    )) as { id: number; url: string; }[];

  const hashLinks = layout === PageType.Brands
    ? domains
        .map((domainItem: DomainDto) => (
          {
              id: domainItem.id,
              url: utilPagePathGenerator(
                {
                  brand,
                  publisher,
                  slug,
                },
                {
                  absolute: true,
                  domain: domainItem.name,
                  public: true,
                  withHash: true,
                },
              ),
          }
        )) as { id: number; url: string; }[]
    : [];

  return (
    <div className={styles.success_page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.icon}>
            <CheckIcon />
          </div>
          <h1 className={styles.title}>Page Created Successfully!</h1>
          <p className={styles.subtitle}>Your page is now live. Use the links below to access or share it.</p>
        </div>

        <div className={styles.sections}>
          <LinkSection
            description="Use this link to edit your page in the admin panel"
            title="Editing Link"
          >
            <LinkItem
              isInternal
              to={localeLinkToEditing}
              url={absoluteLinkToEditing}
            />
          </LinkSection>

          <LinkSection
            description="Share these links with your audience"
            title="Public Links"
          >
            {_.uniqBy(publicLinks, 'url').map((link) => (
              <LinkItem key={link.id} url={link.url} />
            ))}
          </LinkSection>

          {hashLinks.length > 0 && (
            <LinkSection
              description="Public links without brand in URL"
              title="Alternative Links"
            >
              {_.uniqBy(hashLinks, 'url').map((link) => (
                <LinkItem key={link.id} url={link.url} />
              ))}
            </LinkSection>
          )}
        </div>

        <div className={styles.actions}>
          <NavLink className={styles.btn_secondary} to={localeLinkToEditing}>
            Edit Page
          </NavLink>
          <NavLink className={styles.btn_primary} to="/pages">
            View All Pages
          </NavLink>
        </div>
      </div>
    </div>
  );
};
